import { useState } from 'react';
import { act, formatNumber } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad } from './kit';

interface Trunk {
  id: string;
  name: string;
  host: string;
  port: number;
  transport: 'udp' | 'tcp' | 'tls';
  codecs: string[];
  max_channels: number;
  auth_mode: 'ip' | 'credentials';
  auth_username: string | null;
  register: boolean;
  status: string;
}
interface Did {
  id: string;
  number: string;
  label: string | null;
  queue_id: string | null;
  queue_name: string | null;
  ivr_flow_id: string | null;
  ivr_flow_name: string | null;
  test_extension: string | null;
  trunk_name: string | null;
}
interface Named {
  id: string;
  name: string;
}

const CODECS = ['alaw', 'ulaw', 'g722', 'opus', 'g729'] as const;

// ------------------------------------------------------------------ carriers
export function Carriers() {
  const [trunks, reload] = useLoad<Trunk[]>('/trunks');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<Trunk | null>(null);

  return (
    <>
      <SectionHeader
        title="Carriers"
        lead="The SIP trunks your calls arrive on and leave by. Ask your carrier to send calls to this server's address on its SIP port."
        action={<AddButton onClick={() => setAdding(true)}>Add carrier</AddButton>}
      />
      <Table head={['Carrier', 'Address', 'Sign-in', 'Channels', 'Codecs', 'Status']} empty={trunks?.length === 0 && 'No carrier yet. Add one to receive and make calls.'}>
        {(trunks ?? []).map((t) => (
          <Row key={t.id} onClick={() => setOpen(t)}>
            <Cell className="font-medium">{t.name}</Cell>
            <Cell className="tabular">
              {t.host}:{t.port} <span className="text-ink-3">{t.transport.toUpperCase()}</span>
            </Cell>
            <Cell>{t.auth_mode === 'ip' ? 'By IP address' : `${t.auth_username}${t.register ? ', registers' : ''}`}</Cell>
            <Cell className="tabular">{t.max_channels}</Cell>
            <Cell>{t.codecs.join(', ')}</Cell>
            <Cell>
              <TrunkStatus status={t.status} registers={t.register} />
            </Cell>
          </Row>
        ))}
      </Table>
      {adding ? (
        <CarrierDrawer
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
      {open ? (
        <Drawer
          title={open.name}
          open
          onClose={() => setOpen(null)}
          footer={
            <>
              <Button onClick={() => setOpen(null)}>Close</Button>
              <span className="ml-auto">
                <ConfirmButton
                  confirmText="Remove carrier"
                  onConfirm={async () => {
                    const ok = await act(() => api('DELETE', `/trunks/${open.id}`).then(() => true), `${open.name} removed`);
                    if (ok) {
                      setOpen(null);
                      reload();
                    }
                  }}
                >
                  Remove
                </ConfirmButton>
              </span>
            </>
          }
        >
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <Fact k="Address" v={`${open.host}:${open.port}`} />
            <Fact k="Transport" v={open.transport.toUpperCase()} />
            <Fact k="Sign-in" v={open.auth_mode === 'ip' ? 'By IP address' : `Username ${open.auth_username}`} />
            <Fact k="Registers" v={open.register ? 'Yes' : 'No'} />
            <Fact k="Channels" v={String(open.max_channels)} />
            <Fact k="Codecs" v={open.codecs.join(', ')} />
          </dl>
          <p className="mt-6 text-sm text-ink-2">
            To change a carrier's details, remove it and add it again. Numbers stay assigned to their queues.
          </p>
        </Drawer>
      ) : null}
    </>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-3">{k}</dt>
      <dd className="mt-0.5">{v}</dd>
    </div>
  );
}

function TrunkStatus({ status, registers }: { status: string; registers: boolean }) {
  if (status === 'online') return <Pill tone="good">Reachable</Pill>;
  if (status === 'offline') return <Pill tone="bad">{registers ? 'Not registered' : 'Unreachable'}</Pill>;
  return <Pill>{registers ? 'Registering' : 'Ready'}</Pill>;
}

function CarrierDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: '', host: '', port: 5060, transport: 'udp' as Trunk['transport'], maxChannels: 30, codecs: ['alaw', 'ulaw'] as string[] });
  const [useAuth, setUseAuth] = useState(false);
  const [auth, setAuth] = useState({ username: '', password: '', realm: '', register: true, fromDomain: '' });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const body = {
      ...f,
      auth: useAuth
        ? {
            username: auth.username,
            password: auth.password,
            register: auth.register,
            realm: auth.realm || undefined,
            fromDomain: auth.fromDomain || undefined,
          }
        : undefined,
    };
    const ok = await act(() => api('POST', '/trunks', body), `${f.name} added`);
    setBusy(false);
    if (ok) onDone();
  }

  const valid = f.name.trim().length >= 2 && /^[a-zA-Z0-9.-]{3,}$/.test(f.host) && f.codecs.length > 0 && (!useAuth || (auth.username && auth.password));

  return (
    <Drawer
      title="Add a carrier"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Adding…' : 'Add carrier'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Name" hint="For your reference, for example the carrier's name.">
        {(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
      </Field>
      <div className="grid grid-cols-[1fr_96px] gap-3">
        <Field label="Carrier address" hint="Host name or IP address">
          {(id) => <input id={id} className={`${inputClass} tabular`} value={f.host} placeholder="sip.carrier.example" onChange={(e) => setF({ ...f, host: e.target.value.trim() })} />}
        </Field>
        <Field label="Port">
          {(id) => <input id={id} type="number" className={`${inputClass} tabular`} value={f.port} onChange={(e) => setF({ ...f, port: Number(e.target.value) || 5060 })} />}
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Transport">
          {(id) => (
            <select id={id} className={inputClass} value={f.transport} onChange={(e) => setF({ ...f, transport: e.target.value as Trunk['transport'] })}>
              <option value="udp">UDP</option>
              <option value="tcp">TCP</option>
              <option value="tls">TLS</option>
            </select>
          )}
        </Field>
        <Field label="Channels" hint="Concurrent calls you bought">
          {(id) => <input id={id} type="number" min={1} className={`${inputClass} tabular`} value={f.maxChannels} onChange={(e) => setF({ ...f, maxChannels: Math.max(1, Number(e.target.value) || 1) })} />}
        </Field>
      </div>
      <p className="mb-2 text-sm font-medium">Codecs</p>
      <div className="mb-5 flex flex-wrap gap-2">
        {CODECS.map((c) => {
          const on = f.codecs.includes(c);
          return (
            <button
              key={c}
              type="button"
              aria-pressed={on}
              onClick={() => setF({ ...f, codecs: on ? f.codecs.filter((x) => x !== c) : [...f.codecs, c] })}
              className={`rounded-lg border px-3 py-1.5 text-sm ${on ? 'border-ion bg-ion/15' : 'border-line text-ink-2'}`}
            >
              {c === 'alaw' ? 'G.711 a-law' : c === 'ulaw' ? 'G.711 µ-law' : c === 'g722' ? 'G.722' : c === 'g729' ? 'G.729' : 'Opus'}
            </button>
          );
        })}
      </div>

      <Toggle label="The carrier gave me a SIP username and password" checked={useAuth} onChange={setUseAuth} />
      {useAuth ? (
        <div className="rounded-xl border border-line p-4">
          <Field label="SIP username">{(id) => <input id={id} className={inputClass} value={auth.username} autoComplete="off" onChange={(e) => setAuth({ ...auth, username: e.target.value })} />}</Field>
          <Field label="SIP password">
            {(id) => <input id={id} type="password" className={inputClass} value={auth.password} autoComplete="new-password" onChange={(e) => setAuth({ ...auth, password: e.target.value })} />}
          </Field>
          <Field label="Realm" hint="Optional. Only if the carrier asks for one.">
            {(id) => <input id={id} className={inputClass} value={auth.realm} onChange={(e) => setAuth({ ...auth, realm: e.target.value })} />}
          </Field>
          <Field label="From domain" hint="Optional. Some carriers require their domain in the From header.">
            {(id) => <input id={id} className={inputClass} value={auth.fromDomain} onChange={(e) => setAuth({ ...auth, fromDomain: e.target.value })} />}
          </Field>
          <Toggle label="Register with the carrier to receive calls" checked={auth.register} onChange={(v) => setAuth({ ...auth, register: v })} />
        </div>
      ) : (
        <p className="text-sm text-ink-2">Without a username, the carrier recognises you by this server's IP address. Ask them to allow it.</p>
      )}
    </Drawer>
  );
}

// ------------------------------------------------------------------ numbers
interface FlowOption {
  id: string;
  name: string;
  live_version: number | null;
}

export function Numbers() {
  const [dids, reload] = useLoad<Did[]>('/dids');
  const [queues] = useLoad<Named[]>('/queues');
  const [flows] = useLoad<FlowOption[]>('/ivr-flows');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Did | null>(null);
  const done = () => {
    setAdding(false);
    setEditing(null);
    reload();
  };

  return (
    <>
      <SectionHeader
        title="Numbers"
        lead="Phone numbers customers call. Each one rings a queue directly or starts an IVR flow. Give a number a test extension to call it from any softphone before a carrier is connected."
        action={<AddButton onClick={() => setAdding(true)}>Add number</AddButton>}
      />
      <Table head={['Number', 'Label', 'Calls go to', 'Test extension', '']} empty={dids?.length === 0 && 'No numbers yet.'}>
        {(dids ?? []).map((d) => (
          <Row key={d.id} onClick={() => setEditing(d)}>
            <Cell className="tabular font-medium">{formatNumber(d.number)}</Cell>
            <Cell>{d.label ?? <span className="text-ink-3">None</span>}</Cell>
            <Cell>
              {d.ivr_flow_name ? (
                <>
                  <Pill>IVR</Pill> {d.ivr_flow_name}
                </>
              ) : (
                d.queue_name ?? <span className="text-break">No queue</span>
              )}
            </Cell>
            <Cell className="tabular">{d.test_extension ?? <span className="text-ink-3">None</span>}</Cell>
            <Cell className="text-right">
              <span onClick={(e) => e.stopPropagation()}>
                <ConfirmButton
                  confirmText="Remove number"
                  onConfirm={async () => {
                    const ok = await act(() => api('DELETE', `/dids/${d.id}`).then(() => true), `${formatNumber(d.number)} removed`);
                    if (ok) reload();
                  }}
                >
                  Remove
                </ConfirmButton>
              </span>
            </Cell>
          </Row>
        ))}
      </Table>
      {adding || editing ? (
        <NumberDrawer did={editing} queues={queues ?? []} flows={flows ?? []} onClose={() => (setAdding(false), setEditing(null))} onDone={done} />
      ) : null}
    </>
  );
}

function NumberDrawer({
  did,
  queues,
  flows,
  onClose,
  onDone,
}: {
  did: Did | null;
  queues: Named[];
  flows: FlowOption[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [number, setNumber] = useState(did?.number ?? '');
  const [label, setLabel] = useState(did?.label ?? '');
  const [testExt, setTestExt] = useState(did?.test_extension ?? '');
  const [route, setRoute] = useState<'queue' | 'ivr'>(did?.ivr_flow_id ? 'ivr' : 'queue');
  const [queueId, setQueueId] = useState(did?.queue_id ?? queues[0]?.id ?? '');
  const published = flows.filter((f) => f.live_version);
  const [flowId, setFlowId] = useState(did?.ivr_flow_id ?? published[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const digits = number.replace(/\D/g, '');
  const target = route === 'queue' ? { queueId } : { ivrFlowId: flowId };

  async function save() {
    setBusy(true);
    const ok = did
      ? await act(() => api('PATCH', `/dids/${did.id}`, { ...target, label: label || undefined, testExtension: testExt || null }), `${formatNumber(did.number)} updated`)
      : await act(() => api('POST', '/dids', { number: digits, ...target, label: label || undefined, testExtension: testExt || null }), `${formatNumber(digits)} added`);
    setBusy(false);
    if (ok) onDone();
  }

  return (
    <Drawer
      title={did ? formatNumber(did.number) : 'Add a number'}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={busy || digits.length < 3 || (route === 'queue' ? !queueId : !flowId) || (testExt !== '' && !/^\d{3,6}$/.test(testExt))}>
            {busy ? 'Saving…' : did ? 'Save' : 'Add number'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {did ? null : (
        <Field label="Number" hint="As your carrier sends it, usually with the country code: 254700000100.">
          {(id) => <input id={id} inputMode="tel" className={`${inputClass} tabular`} value={number} onChange={(e) => setNumber(e.target.value)} />}
        </Field>
      )}
      <Field label="Label" hint="Optional, for example Main line or Sales.">
        {(id) => <input id={id} className={inputClass} value={label} onChange={(e) => setLabel(e.target.value)} />}
      </Field>
      <Field
        label="Test extension"
        hint="Optional, 3 to 6 digits, e.g. 7000. Dial it from any softphone or the New dialer to call this number as a customer would: no carrier needed."
      >
        {(id) => <input id={id} inputMode="numeric" className={`${inputClass} tabular`} placeholder="7000" value={testExt} maxLength={6} onChange={(e) => setTestExt(e.target.value.replace(/\D/g, ''))} />}
      </Field>
      <p className="mb-1.5 text-sm font-medium">Calls go to</p>
      <div className="mb-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Calls go to">
        {(['queue', 'ivr'] as const).map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={route === r}
            onClick={() => setRoute(r)}
            className={`rounded-lg border px-3 py-2 text-sm ${route === r ? 'border-ion bg-ion/15' : 'border-line text-ink-2'}`}
          >
            {r === 'queue' ? 'A queue' : 'An IVR flow'}
          </button>
        ))}
      </div>
      {route === 'queue' ? (
        <Field label="Queue">
          {(id) => (
            <select id={id} className={inputClass} value={queueId} onChange={(e) => setQueueId(e.target.value)}>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : published.length ? (
        <Field label="IVR flow" hint="Calls use the flow's live version. Publishing a new version changes it for this number too.">
          {(id) => (
            <select id={id} className={inputClass} value={flowId} onChange={(e) => setFlowId(e.target.value)}>
              {published.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} (version {f.live_version})
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : (
        <p className="text-sm text-ink-2">No published IVR flow yet. Build and publish one under IVR flows first.</p>
      )}
    </Drawer>
  );
}
